import React, { useMemo, useState } from 'react';
import { Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { useGovernedMcp } from '../governedMcpStore';
import type { AccessPolicy } from '../governedMcpTypes';
import ConfirmDialog from '../components/ConfirmDialog';
import EmptyState from '../components/EmptyState';
import { fieldClass, ghostBtn, primaryBtn } from '../components/GatewayUi';
import { EffectBadge, RiskBadge, StatusBadge } from '../components/StatusBadges';
import PolicyFormDrawer from '../drawers/PolicyFormDrawer';

export default function PoliciesTab({
  isLight,
  onToast,
}: {
  isLight: boolean;
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  const { state, catalogs, savePolicy, deletePolicy } = useGovernedMcp();
  const [query, setQuery] = useState('');
  const [policy, setPolicy] = useState<AccessPolicy | null | 'new'>(null);
  const [deletePol, setDeletePol] = useState<AccessPolicy | null>(null);

  const policies = useMemo(() => {
    const q = query.trim().toLowerCase();
    return state.policies.filter((item) => !q || `${item.name} ${item.description}`.toLowerCase().includes(q));
  }, [state.policies, query]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`} />
          <input className={`${fieldClass(isLight)} pl-9`} placeholder="Search policies…" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <button type="button" className={primaryBtn()} onClick={() => setPolicy('new')}>
          <Plus size={14} /> Create policy
        </button>
      </div>

      {policies.length === 0 ? (
        <EmptyState
          isLight={isLight}
          title="No access policies"
          description="Bind users, clients, and published tools to Allow, Deny, or Require Approval."
          action={
            <button type="button" className={primaryBtn()} onClick={() => setPolicy('new')}>
              Create policy
            </button>
          }
        />
      ) : (
        <div className={`overflow-x-auto rounded-xl border ${isLight ? 'border-neutral-200 bg-white' : 'border-neutral-800 bg-neutral-900'}`}>
          <table className="w-full text-sm">
            <thead className={isLight ? 'bg-neutral-50 text-neutral-500' : 'bg-neutral-950 text-neutral-400'}>
              <tr className="text-left text-xs uppercase tracking-wide">
                {['Policy', 'Effect', 'Clients', 'Tools', 'Risk', 'Status', ''].map((col) => (
                  <th key={col} className="px-3 py-2.5 font-medium">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {policies.map((item) => (
                <tr key={item.id} className={`border-t ${isLight ? 'border-neutral-100' : 'border-neutral-800'}`}>
                  <td className="px-3 py-3">
                    <p className={`font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>{item.name}</p>
                    <p className={`text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{item.description}</p>
                  </td>
                  <td className="px-3 py-3">
                    <EffectBadge effect={item.effect} />
                  </td>
                  <td className="px-3 py-3">{item.clientIds.length || 'Any'}</td>
                  <td className="px-3 py-3">{item.publishedCapabilityIds.length || 'Any'}</td>
                  <td className="px-3 py-3">
                    <RiskBadge level={item.riskLevel} />
                  </td>
                  <td className="px-3 py-3">
                    <StatusBadge status={item.status} />
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <button type="button" className={ghostBtn(isLight) + ' px-2 mr-1'} onClick={() => setPolicy(item)} aria-label="Edit">
                      <Pencil size={14} />
                    </button>
                    <button type="button" className={ghostBtn(isLight) + ' px-2 text-red-400'} onClick={() => setDeletePol(item)} aria-label="Delete">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PolicyFormDrawer
        open={policy !== null}
        initial={policy && policy !== 'new' ? policy : null}
        users={catalogs.users}
        groups={catalogs.groups}
        clients={state.clients}
        published={state.published}
        isLight={isLight}
        onClose={() => setPolicy(null)}
        onSave={(item) => {
          savePolicy(item);
          setPolicy(null);
          onToast('success', `Policy “${item.name}” saved`);
        }}
      />
      <ConfirmDialog
        open={Boolean(deletePol)}
        title="Delete policy?"
        description={deletePol ? `Remove “${deletePol.name}”? Subsequent MCP calls will be re-evaluated without it.` : ''}
        confirmLabel="Delete"
        danger
        isLight={isLight}
        onCancel={() => setDeletePol(null)}
        onConfirm={() => {
          if (deletePol) {
            deletePolicy(deletePol.id);
            onToast('success', 'Policy deleted');
          }
          setDeletePol(null);
        }}
      />
    </div>
  );
}
